import { CommonModule } from '@angular/common';
import {
	Component,
	ElementRef,
	EventEmitter,
	Inject,
	Input,
	Output,
	Renderer2,
	ViewChild,
	forwardRef,
} from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatTabsModule } from '@angular/material/tabs';
import { AtsRegex } from '@ats/utility';
import { LOC_INJECTOR, LocalizationProvider } from '@cm/core/localization';
import { SettingsService } from '@cm/core/settings';
import { TINYMCE } from '@cm/shared/ui/tinymce';
import { EditorModule, TINYMCE_SCRIPT_SRC } from '@tinymce/tinymce-angular';
import { LocalizePipe } from '../../pipes/localize.pipe';
import { sharedLocalizationLocs } from '../../shared.locs';
import { FormLocType, localizationFormLocs } from './html-localize-form.locs';

@Component({
    selector: 'cm-html-localize-form',
    templateUrl: './html-localize-form.component.html',
    styleUrls: ['./html-localize-form.component.scss'],
    providers: [
        { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => HtmlLocalizeFormComponent), multi: true },
        { provide: TINYMCE_SCRIPT_SRC, useValue: 'tinymce/tinymce.min.js' },
        LocalizationProvider({ ...localizationFormLocs, ...sharedLocalizationLocs }),
    ],
    imports: [CommonModule, MatTabsModule, FormsModule, EditorModule, LocalizePipe]
})
export class HtmlLocalizeFormComponent implements ControlValueAccessor {
	@ViewChild('editor', { read: ElementRef }) editor!: ElementRef;
	@Input() defaultValue: string = '';
	@Output() codeEditorClose = new EventEmitter<boolean>();
	tiny: typeof TINYMCE;
	private value: string = '';
	public get locValue(): string {
		return this.value;
	}
	public set locValue(value: string) {
		if (value !== this.value) {
			this.value = value;
			this.onChange(value);
		}
	}

	constructor(
		@Inject(LOC_INJECTOR) public readonly locs: FormLocType,
		private readonly renderer: Renderer2,
		private readonly settings: SettingsService
	) {
		this.tiny = {
			API_KEY: TINYMCE.API_KEY,
			CONFIG: {
				...TINYMCE.CONFIG,
				auto_focus: 'tiny-input',
				setup: this.getEditorWhitespaceScrubberConfig(),
			},
		};
	}

	onChange = (value: string) => {};
	onTouched = () => {};

	writeValue(value: string): void {
		this.locValue = value;
	}

	registerOnChange(fn: any): void {
		this.onChange = fn;
	}
	registerOnTouched(fn: any): void {
		this.onTouched = fn;
	}
	setDisabledState(isDisabled: boolean): void {
		if (this.editor) {
			this.renderer.setProperty(this.editor.nativeElement, 'disabled', isDisabled);
		}
	}

	private getEditorWhitespaceScrubberConfig = (): ((editor: any) => any) => {
		return editor =>
			editor.on('ExecCommand', (e: any) => {
				if (e.command === 'mceCodeEditor') {
					e.target.on('CloseWindow', (e2: any) => {
						const data = e2.dialog.getData();
						if (data) {
							this.codeEditorClose.emit(
								data.code &&
									this.value &&
									data.code.replace(AtsRegex.WHITESPACE, '').toLowerCase() !==
										this.value.replace(AtsRegex.WHITESPACE, '').toLowerCase()
							);
						} else {
							this.codeEditorClose.emit(false);
						}
						e.target.off('CloseWindow');
					});
				}
			});
	};
}
